import {
  CreateStaffSchema,
  manageableRoles,
  type Staff,
  type StaffRole,
} from '@atlas-control/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { type Control, type FieldValues, type Path, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { PageHeader } from '@/components/page-header';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { staffQuery, useStaffMutations } from '@/features/staff/api';
import { ROLE_LABEL, useMe } from '@/lib/auth';
import { formatDateTime, formatRelative } from '@/lib/format';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/staff/')({
  beforeLoad: ({ context }) => guard(context.me, 'staff.manage_auditors', 'staff.manage_all'),
  loader: ({ context }) => context.queryClient.ensureQueryData(staffQuery),
  component: StaffPage,
});

type Pending =
  { kind: 'disable' | 'enable' | 'reset'; staff: Staff } | { kind: 'edit'; staff: Staff } | null;

function StaffPage() {
  const { user } = useMe();
  const { data: staff } = useSuspenseQuery(staffQuery);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const roles = manageableRoles(user.role);

  return (
    <>
      <PageHeader
        title="Staff"
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/activity">Activity</Link>
            </Button>
            <Button onClick={() => setAdding(true)}>
              <Plus /> Add staff
            </Button>
          </>
        }
      />

      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last sign-in</TableHead>
              <TableHead>Created by</TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staff.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  No accounts yet. Use “Add staff” to invite someone.
                </TableCell>
              </TableRow>
            )}
            {staff.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">
                  {s.name}
                  {s.id === user.id && (
                    <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{s.email}</TableCell>
                <TableCell>{ROLE_LABEL[s.role]}</TableCell>
                <TableCell>
                  {s.status === 'disabled' ? (
                    <StatusBadge tone="danger">Disabled</StatusBadge>
                  ) : s.passwordSet ? (
                    <StatusBadge tone="success">Active</StatusBadge>
                  ) : (
                    <StatusBadge tone="warning">Invitation pending</StatusBadge>
                  )}
                </TableCell>
                <TableCell title={formatDateTime(s.lastSignInAt)}>
                  {formatRelative(s.lastSignInAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {s.createdBy?.name ?? 'System'}
                </TableCell>
                <TableCell>
                  <RowActions staff={s} isSelf={s.id === user.id} onAction={setPending} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <AddStaffDialog open={adding} onOpenChange={setAdding} roles={roles} />
      <EditStaffDialog
        staff={pending?.kind === 'edit' ? pending.staff : null}
        roles={roles}
        canChangeRole={user.role === 'developer'}
        onClose={() => setPending(null)}
      />
      <StatusConfirm pending={pending} onClose={() => setPending(null)} />
    </>
  );
}

function RowActions({
  staff,
  isSelf,
  onAction,
}: {
  staff: Staff;
  isSelf: boolean;
  onAction: (p: Pending) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions for ${staff.name}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onAction({ kind: 'edit', staff })}>Edit</DropdownMenuItem>
        <DropdownMenuItem
          disabled={staff.status === 'disabled'}
          onSelect={() => onAction({ kind: 'reset', staff })}
        >
          {staff.passwordSet ? 'Send password reset link' : 'Resend invitation'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {staff.status === 'active' ? (
          <DropdownMenuItem
            variant="destructive"
            disabled={isSelf}
            onSelect={() => onAction({ kind: 'disable', staff })}
          >
            Disable account
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => onAction({ kind: 'enable', staff })}>
            Enable account
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type AddValues = z.infer<typeof CreateStaffSchema>;

function AddStaffDialog({
  open,
  onOpenChange,
  roles,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  roles: StaffRole[];
}) {
  const { create } = useStaffMutations();
  const form = useForm<AddValues>({
    resolver: zodResolver(CreateStaffSchema),
    defaultValues: { name: '', email: '', role: 'auditor' },
  });

  function onSubmit(values: AddValues) {
    create.mutate(values, {
      onSuccess: () => {
        toastSuccess(`Invitation sent to ${values.email}`);
        form.reset();
        onOpenChange(false);
      },
      onError: (err) => toastError(err),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add staff</DialogTitle>
          <DialogDescription>
            They get an email with a link to set their password (valid for 24 hours).
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            id="add-staff"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <RoleField control={form.control} roles={roles} />
          </form>
        </Form>
        <DialogFooter>
          <Button type="submit" form="add-staff" disabled={create.isPending}>
            {create.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleField<T extends FieldValues & { role: StaffRole }>({
  control,
  roles,
  disabled,
}: {
  control: Control<T>;
  roles: StaffRole[];
  disabled?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={'role' as Path<T>}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Role</FormLabel>
          <Select
            value={field.value}
            onValueChange={field.onChange}
            disabled={disabled || roles.length < 2}
          >
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>
                  {ROLE_LABEL[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function EditStaffDialog({
  staff,
  roles,
  canChangeRole,
  onClose,
}: {
  staff: Staff | null;
  roles: StaffRole[];
  canChangeRole: boolean;
  onClose: () => void;
}) {
  const { user } = useMe();
  const { update } = useStaffMutations();
  const form = useForm<{ name: string; role: StaffRole }>({
    values: staff ? { name: staff.name, role: staff.role } : { name: '', role: 'auditor' },
  });

  if (!staff) return null;
  const roleLocked = !canChangeRole || staff.id === user.id;

  function onSubmit(values: { name: string; role: StaffRole }) {
    if (!staff) return;
    update.mutate(
      {
        id: staff.id,
        ...(values.name.trim() !== staff.name ? { name: values.name.trim() } : {}),
        ...(values.role !== staff.role ? { role: values.role } : {}),
      },
      {
        onSuccess: () => {
          toastSuccess('Account updated');
          onClose();
        },
        onError: (err) => toastError(err),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {staff.name}</DialogTitle>
          <DialogDescription>{staff.email}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id="edit-staff" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: 'Enter a name' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <RoleField control={form.control} roles={roles} disabled={roleLocked} />
            {roleLocked && (
              <p className="text-xs text-muted-foreground">
                {staff.id === user.id
                  ? 'You cannot change your own role.'
                  : 'Only a developer can change roles.'}
              </p>
            )}
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="submit"
            form="edit-staff"
            disabled={update.isPending || !form.formState.isDirty}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusConfirm({ pending, onClose }: { pending: Pending; onClose: () => void }) {
  const { setStatus, resetPassword } = useStaffMutations();
  if (!pending || pending.kind === 'edit') return null;
  const { staff } = pending;

  const copy = {
    disable: {
      title: `Disable ${staff.name}?`,
      description:
        'They are signed out everywhere right away and cannot sign in until the account is enabled again. Their history is kept.',
      confirm: 'Disable account',
    },
    enable: {
      title: `Enable ${staff.name}?`,
      description: 'They can sign in again with their existing password.',
      confirm: 'Enable account',
    },
    reset: {
      title: staff.passwordSet
        ? `Send a reset link to ${staff.name}?`
        : `Resend the invitation to ${staff.name}?`,
      description: `An email with a link valid for 24 hours goes to ${staff.email}.`,
      confirm: 'Send link',
    },
  }[pending.kind];

  const busy = setStatus.isPending || resetPassword.isPending;
  function confirm() {
    const done = {
      onSuccess: () => {
        toastSuccess(pending?.kind === 'reset' ? 'Link sent' : 'Account updated');
        onClose();
      },
      onError: (err: unknown) => toastError(err),
    };
    if (pending?.kind === 'reset') resetPassword.mutate(staff.id, done);
    else
      setStatus.mutate(
        { id: staff.id, status: pending?.kind === 'enable' ? 'active' : 'disabled' },
        done,
      );
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={copy.title}
      description={copy.description}
      confirmLabel={copy.confirm}
      destructive={pending.kind === 'disable'}
      pending={busy}
      onConfirm={confirm}
    />
  );
}
